import PropTypes from "prop-types";
import { forwardRef, memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import BusinessIcon from "@mui/icons-material/Business";
import ActiveCompanyIcon from "@mui/icons-material/CheckRounded";
import ExpandIcon from "@mui/icons-material/ExpandMoreRounded";
import SearchBoxIcon from "@mui/icons-material/Search";
import HeaderIcon from "@mui/icons-material/SwapHorizRounded";
import {
  alpha,
  Avatar,
  Box,
  Divider,
  InputBase,
  ListItem,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/system";

const motion = {
  fast: 120,
  base: 160,
  slow: 200,
  easing: "cubic-bezier(0.2, 0, 0, 1)",
};

function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0]?.toUpperCase())
    .join("");
}

/* Primär badge (clean, inte MUI chip) */
function PrimaryBadge() {
  return (
    <Box
      sx={{
        px: 0.9,
        py: "2px",
        borderRadius: 1,
        fontSize: 11,
        fontWeight: 600,
        background: "rgba(214,158,46,0.16)",
        color: "#8a6420",
        lineHeight: 1,
      }}
    >
      Primär
    </Box>
  );
}

const OrganizationItem = forwardRef(function OrganizationItem({ org, active, highlighted, onSelect }, ref) {
  const color = org?.color || "#d71920";

  return (
    <ListItem
      ref={ref}
      id={`org-option-${org.id}`}
      role="option"
      aria-selected={active || highlighted}
      onClick={() => onSelect(org.id)}
      sx={{
        position: "relative",
        contain: "layout paint",
        p: 0,
        mb: 0.75,
        borderRadius: 2,
        cursor: active ? "default" : "pointer",
        userSelect: "none",
        transition: `opacity ${motion.fast}ms ${motion.easing}, border-color ${motion.fast}ms ${motion.easing}, background-color ${motion.fast}ms ${motion.easing}`,
        whiteSpace: "break-spaces",
        "&:focus": {
          outline: "none",
        },
        "&.Mui-focusVisible": {
          outline: "none",
        },
        color: highlighted
          ? "rgba(15,23,42,0.85)"
          : undefined,
        border: highlighted
          ? "2px solid rgba(15,23,42,0.35)"
          : "2px solid transparent",
        boxSizing: "border-box",
        background: active
          ? "rgba(15,23,42,0.12)"
          : "transparent",
        "&:hover": {
          background: active
            ? "rgba(15,23,42,0.12)"
            : "rgba(15,23,42,0.06)",
        },
      }}
    >
      <Box sx={{ px: 1.5, py: 1.2, flex: 1, }}>
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          sx={{
            opacity: (active || highlighted) ? 1 : 0.82,
            transition: `opacity ${motion.fast}ms ${motion.easing}`,
            "&:hover": {
              opacity: 1,
            },
          }}
        >

          <Avatar
            src={org.logoUrl || undefined}
            sx={{
              width: 34,
              height: 34,
              fontSize: 13,
              fontWeight: 500,
              bgcolor: "rgba(15,23,42,0.05)",
              color: "rgba(15,23,42,0.7)",
              border: "1px solid rgba(15,23,42,0.06)",
            }}
          >
            {!org.logoUrl && (
              getInitials(org.name) || <BusinessIcon fontSize="small" />
            )}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>

            {/* Company name + Primary company badge */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                sx={{
                  fontSize: 14.5,
                  fontWeight: 600,
                  color: "rgba(15,23,42,0.96)",
                }}
              >
                {org.name}
              </Typography>

              {org.isPrimary && <PrimaryBadge />}
            </Stack>

            {/* Subtext */}
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: active
                  ? "rgba(15,23,42,0.85)"
                  : "rgba(15,23,42,0.6)",
                transition: "color .15s ease",
              }}
            >
              {active
                ? "Aktiv nu"
                : "Växla till denna organisation"}
            </Typography>
          </Box>

          {/* Active company icon */}
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: active ? "rgba(15,23,42,0.04)" : "transparent",
              color: active ? color : "transparent",
              transform: active ? "scale(1)" : "scale(0.85)",
              transition: `transform ${motion.fast}ms ${motion.easing}, background-color ${motion.base}ms ${motion.easing}, color ${motion.base}ms ${motion.easing}`,
              "&:hover": {
                transform: active ? "scale(1.05)" : "scale(0.9)",
              },
            }}
          >
            <ActiveCompanyIcon sx={{ fontSize: 18 }} />
          </Box>
        </Stack>
      </Box>
    </ListItem>
  );
});

const MemoOrganizationItem = memo(
  OrganizationItem,
  (prev, next) => {
    return (
      prev.org.id === next.org.id &&
      prev.active === next.active &&
      prev.highlighted === next.highlighted
    );
  }
);

export default function OrganizationSelector({
  organizations,
  activeOrganizationId,
  onChange,
  compact,
}) {
  const theme = useTheme();

  const inputRef = useRef(null);
  const itemRefs = useRef(new Map());

  const [anchorEl, setAnchorEl] = useState(null);
  const [highlightedId, setHighlightedId] = useState(null);
  const [inputValue, setInputValue] = useState("");

  const deferredInput = useDeferredValue(inputValue);

  const open = Boolean(anchorEl);

  const activeOrg =
    organizations.find(o => String(o.id) === String(activeOrganizationId)) ??
    organizations[0] ??
    null;

  const filteredList = useMemo(() => {
    const s = deferredInput.toLowerCase();
    return organizations.filter(o =>
      o.name.toLowerCase().includes(s)
    );
  }, [organizations, deferredInput]);

  const highlightedIndex = useMemo(() => {
    if (!highlightedId)
      return -1;
    return filteredList.findIndex(o => String(o.id) === String(highlightedId));
  }, [filteredList, highlightedId]);

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);

  useEffect(() => {
    if (!open)
      return;

    focusInput();
    setInputValue("");
  }, [open]);

  useEffect(() => {
    if (!open || !filteredList.length)
      return;

    const found = filteredList.find(
      o => String(o.id) === String(activeOrganizationId)
    );

    setHighlightedId(found ? found.id : null);
  }, [open, filteredList, activeOrganizationId]);

  useEffect(() => {
    if (!open || !highlightedId)
      return;

    const raf = requestAnimationFrame(() => {
      const el = itemRefs.current.get(String(highlightedId));
      el?.scrollIntoView({ block: "nearest" });
    });

    return () => cancelAnimationFrame(raf);
  }, [highlightedId, open]);

  const moveHighlight = (delta) => {
    if (!filteredList.length) {
      setHighlightedId(null);
      return;
    }

    const currentIndex = filteredList.findIndex(
      o => String(o.id) === String(highlightedId)
    );

    const safeIndex = currentIndex;

    let nextIndex;

    if (safeIndex < 0) {
      nextIndex = delta > 0 ? 0 : filteredList.length - 1;
    } else {
      nextIndex = (safeIndex + delta + filteredList.length) % filteredList.length;
    }

    setHighlightedId(filteredList[nextIndex]?.id ?? null);
  };

  const handleChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (!filteredList.length)
      return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        moveHighlight(1);
        focusInput();
        break;

      case "ArrowUp":
        e.preventDefault();
        moveHighlight(-1);
        focusInput();
        break;

      case "Enter": {
        e.preventDefault();

        if (!highlightedId)
          return;

        handleSelect(highlightedId);
        break;
      }

      case "Escape":
        setAnchorEl(null);
        break;

      case "Tab": {
        setAnchorEl(null);
        return;
      }
    }
  };

  const handleSelect = (orgId) => {
    setAnchorEl(null);
    if (String(orgId) !== String(activeOrganizationId)) {
      onChange?.(orgId, false);
    }
  };

  const setItemRef = useCallback(
    (id) => (el) => {
      if (el) {
        itemRefs.current.set(String(id), el);
      } else {
        itemRefs.current.delete(String(id));
      }
    },
    []
  );

  const safeIndex =
    highlightedIndex >= 0 && highlightedIndex < filteredList.length
      ? highlightedIndex
      : -1;

  return (
    <>
      {/* BUTTON */}
      <Box
        onClick={e => setAnchorEl(e.currentTarget)}
        sx={{
          minWidth: compact ? 160 : 220,
          height: 40,
          px: 1,
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          gap: 0.7,
          cursor: "pointer",
          background: alpha(theme.palette.common.grey, 0.25),
          border: "1px solid rgba(255,255,255,0.18)",
          transition: `background-color ${motion.fast}ms ${motion.easing}`,
          "&:hover": {
            background: alpha(theme.palette.common.grey, 0.35),
          },
        }}
      >
        <Avatar
          src={activeOrg?.logoUrl || undefined}
          sx={{
            width: 28,
            height: 28,
            fontSize: 11
          }}
        >
          {!activeOrg?.logoUrl && getInitials(activeOrg?.name)}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {activeOrg?.name ?? "Välj organisation"}
          </Typography>
        </Box>

        <ExpandIcon sx={{ fontSize: 18 }} />
      </Box>

      {/* DROPDOWN */}
      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 420,
            borderRadius: 2,
            boxShadow: "0 30px 80px rgba(15,23,42,0.18)",
            border: "1px solid rgba(15,23,42,0.08)",
          },
        }}
      >
        {/* HEADER */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Stack spacing={0.3}>
            <Stack direction="row" spacing={1.2} alignItems="center">
              <HeaderIcon
                sx={{ fontSize: 18, color: "rgba(15,23,42,0.5)" }}
              />
              <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
                Byt organisation
              </Typography>
            </Stack>

            <Typography
              sx={{
                fontSize: 13,
                color: "rgba(15,23,42,0.6)",
                ml: 3.6,
              }}
            >
              Välj vilken organisation du vill arbeta i just nu
            </Typography>
          </Stack>
        </Box>

        {/* SEARCH */}
        <Box sx={{ px: 2, pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.2,
              py: 0.6,
              borderRadius: 2,
              background: "rgba(15,23,42,0.05)",
            }}
          >
            <SearchBoxIcon sx={{ fontSize: 16, color: "rgba(15,23,42,0.5)" }} />
            <InputBase
              inputRef={inputRef}
              role="combobox"
              aria-autocomplete="list"
              aria-controls="org-listbox"
              aria-expanded={open}
              aria-haspopup="listbox"
              aria-label="Välj organisation"
              aria-activedescendant={
                highlightedId
                  ? `org-option-${highlightedId}`
                  : undefined
              }
              placeholder="Sök organisation..."
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              value={inputValue}
              sx={{
                fontSize: 13,
                flex: 1,
              }}
            />
          </Box>
        </Box>

        <Divider />

        {/* LIST */}
        <Box
          id="org-listbox"
          role="listbox"
          aria-label="Organisationer"
          sx={{ p: 1.2, maxHeight: 360, overflowY: "auto", overscrollBehavior: "contain" }}
          tabIndex={-1}
          onMouseDown={(e) => e.preventDefault()}
        >
          {filteredList.length === 0 ? (
            <Box
              role="presentation"
              aria-disabled="true"
              aria-live="polite"
              sx={{ px: 1.5, py: 2, color: "rgba(15,23,42,0.6)", opacity: 0.7, fontSize: 13, fontStyle: "italic", }}
            >
              Inga träffar
            </Box>
          ) : (
            filteredList.map((org) => (
              <MemoOrganizationItem
                ref={setItemRef(org.id)}
                key={String(org.id)}
                org={org}
                active={String(org.id) === String(activeOrganizationId)}
                highlighted={String(org.id) === String(highlightedId)}
                onSelect={handleSelect}
              />
            ))
          )}
        </Box>
      </Popover>
    </>
  );
}

OrganizationSelector.propTypes = {
  organizations: PropTypes.array,
  activeOrganizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  compact: PropTypes.bool,
};

OrganizationSelector.defaultProps = {
  organizations: [],
  activeOrganizationId: "",
  onChange: undefined,
  compact: false,
};

OrganizationSelector.displayName = "OrganizationSelector";
